import React from 'react';
import { Sparkles } from 'lucide-react';
import { MAGIC_INITIATE_FREE_CASTS, MAGIC_INITIATE_LISTS, getMagicInitiateGrants, magicInitiateFreeCastsRemaining } from '../../../shared/rules/magicInitiate.mjs';
import spellCatalog from '../../../data/spells.json';

const spellIdFromName = (name) => String(name || '').trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
const catalogById = new Map(spellCatalog.map((spell) => [spellIdFromName(spell.name), spell]));

/**
 * Magic Initiate (feat) level 1 spell: one tile per feat that casts its free use (once per Long Rest, no slot).
 * Slot casts go through the spell list (spells with an engine) or the Features card.
 */
export default function MagicInitiateSpellControls({ form, characterId, combatState, isActiveTurn, blockReason, onCast }) {
  const grants = getMagicInitiateGrants(form).filter((grant) => grant.level === 1);
  if (!grants.length) return null;
  const inCombat = Number.isInteger(combatState?.activeTurn) && Boolean(combatState?.participants?.[combatState.activeTurn]);
  const ledger = combatState?.turnActionLedger?.combatantId === String(characterId) && combatState.turnActionLedger.turnSequence === combatState.turnSequence ? combatState.turnActionLedger : null;
  const actionLeft = !ledger?.actionSpent || Boolean(ledger.actionSurge && !ledger.actionSurge.spent);
  return grants.map((grant) => {
    const spell = catalogById.get(grant.spellId);
    const name = spell?.name || grant.spellId;
    const bonusAction = spell?.actionType === 'bonusAction';
    const remaining = magicInitiateFreeCastsRemaining(form, grant.grantId);
    const resourceLeft = bonusAction ? !ledger?.bonusActionSpent : actionLeft;
    const available = remaining > 0 && !blockReason && (!inCombat || (isActiveTurn && resourceLeft));
    const label = `Magic Initiate (${MAGIC_INITIATE_LISTS[grant.list]})`;
    const title = blockReason || (remaining > 0
      ? `${name} · ${label} · ${bonusAction ? 'Bonus Action' : 'Action'} · no spell slot, once per Long Rest`
      : `${label} · no free ${name} until a Long Rest (cast it with a slot instead)`);
    return (
      <button key={grant.grantId} type="button" data-action-tile={`magic-initiate:${grant.grantId}`} className="combat-hud-resource-tile combat-hud-feature-action-tile" style={{ '--hud-resource-accent': '#b48cf2' }}
        title={title} aria-label={`Cast ${name} free (${label}): ${remaining} of ${MAGIC_INITIATE_FREE_CASTS}`} disabled={!available}
        onClick={() => onCast?.(grant, { name, bonusAction, castingTime: spell?.castingTime || (bonusAction ? '1 bonus action' : '1 action') })}>
        <span className="combat-hud-resource-tile__icon" aria-hidden="true"><Sparkles size={20} /></span>
        <span className="combat-hud-resource-tile__name">{name}</span>
        <span className="combat-hud-resource-tile__value">{remaining}/{MAGIC_INITIATE_FREE_CASTS}</span>
      </button>
    );
  });
}
